import { cbc } from '@noble/ciphers/aes.js';
import { hmac } from '@noble/hashes/hmac.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { zipSync, unzipSync } from 'fflate';
import { normalizeRelativePath } from './filesystem.ts';
import type { MsiInspection } from './msi.ts';
import type { BuildProgress, CompressionLevel, PackageResult, SourceFile } from './types.ts';
import { getToolVersion } from './tool-version.ts';

const encoder = new TextEncoder();
const decoder = new TextDecoder();
// fflate encodes local calendar fields; noon keeps western timezones in 1980.
const ZIP_EPOCH = new Date(1980, 0, 1, 12, 0, 0);

function base64(bytes: Uint8Array): string {
  let text = '';
  for (let i = 0; i < bytes.length; i += 0x8000) text += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(text);
}

function concat(...parts: Uint8Array[]): Uint8Array {
  const result = new Uint8Array(parts.reduce((n, part) => n + part.length, 0));
  let offset = 0;
  for (const part of parts) { result.set(part, offset); offset += part.length; }
  return result;
}

function xmlEscape(value: string): string {
  return value.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&apos;');
}

function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytes;
}

function msiInfoXml(info?: MsiInspection): string {
  if (!info?.parser) return '';
  const bool = (value?: boolean) => String(value === true);
  const context = info.installContext === 'Per-machine' ? 'System' : info.installContext === 'Per-user' ? 'User' : 'Any';
  return `<MsiInfo><MsiProductCode>${xmlEscape(info.productCode || '')}</MsiProductCode><MsiProductVersion>${xmlEscape(info.productVersion || '')}</MsiProductVersion><MsiPackageCode>${xmlEscape(info.packageCode || '')}</MsiPackageCode><MsiUpgradeCode>${xmlEscape(info.upgradeCode || '')}</MsiUpgradeCode><MsiExecutionContext>${context}</MsiExecutionContext><MsiRequiresLogon>${bool(info.requiresLogon)}</MsiRequiresLogon><MsiRequiresReboot>${bool(info.requiresReboot)}</MsiRequiresReboot><MsiIsMachineInstall>${bool(info.isMachineInstall)}</MsiIsMachineInstall><MsiIsUserInstall>${bool(info.isUserInstall)}</MsiIsUserInstall><MsiIncludesServices>${bool(info.includesServices)}</MsiIncludesServices><MsiIncludesODBCDataSource>${bool(info.includesODBCDataSource)}</MsiIncludesODBCDataSource><MsiContainsSystemRegistryKeys>${bool(info.containsSystemRegistryKeys)}</MsiContainsSystemRegistryKeys><MsiContainsSystemFolders>${bool(info.containsSystemFolders)}</MsiContainsSystemFolders><MsiPublisher>${xmlEscape(info.manufacturer || '')}</MsiPublisher></MsiInfo>`;
}

function makeDetectionXml(name: string, setupFile: string, unencryptedSize: number, encryptionKey: Uint8Array, macKey: Uint8Array, iv: Uint8Array, mac: Uint8Array, digest: Uint8Array, msiInfo?: MsiInspection, useMicrosoftCompatibleMetadata = false): Uint8Array {
  const xml = `<ApplicationInfo xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" ToolVersion="${getToolVersion(useMicrosoftCompatibleMetadata)}"><Name>${xmlEscape(name)}</Name><UnencryptedContentSize>${unencryptedSize}</UnencryptedContentSize><FileName>IntunePackage.intunewin</FileName><SetupFile>${xmlEscape(setupFile)}</SetupFile><EncryptionInfo><EncryptionKey>${base64(encryptionKey)}</EncryptionKey><MacKey>${base64(macKey)}</MacKey><InitializationVector>${base64(iv)}</InitializationVector><Mac>${base64(mac)}</Mac><ProfileIdentifier>ProfileVersion1</ProfileIdentifier><FileDigest>${base64(digest)}</FileDigest><FileDigestAlgorithm>SHA256</FileDigestAlgorithm></EncryptionInfo>${msiInfoXml(msiInfo)}</ApplicationInfo>`;
  return encoder.encode(xml);
}

async function makeInnerZip(files: SourceFile[], onProgress: (update: BuildProgress) => void, signal?: AbortSignal, compressionLevel: CompressionLevel = 6): Promise<{ bytes: Uint8Array; inputBytes: number }> {
  const entries: Record<string, Uint8Array> = {};
  let inputBytes = 0;
  for (let index = 0; index < files.length; index += 1) {
    if (signal?.aborted) throw new DOMException('Canceled', 'AbortError');
    const entry = files[index];
    const file = await entry.getFile();
    const bytes = new Uint8Array(await file.arrayBuffer());
    entries[normalizeRelativePath(entry.path)] = bytes;
    inputBytes += bytes.length;
    onProgress({ stage: 'compressing', currentFile: entry.path, processed: index + 1, total: files.length, message: `${index + 1} of ${files.length} files` });
  }
  return { bytes: zipSync(entries, { level: compressionLevel, mtime: ZIP_EPOCH }), inputBytes };
}

export async function createPackage(files: SourceFile[], setupFile: string, outputName: string, onProgress: (update: BuildProgress) => void, signal?: AbortSignal, compressionLevel: CompressionLevel = 6, msiInfo?: MsiInspection, useMicrosoftCompatibleMetadata = false): Promise<PackageResult> {
  const started = performance.now();
  onProgress({ stage: 'scanning', processed: 0, total: files.length, message: 'Preparing source manifest' });
  const normalizedSetup = normalizeRelativePath(setupFile);
  if (!files.some((file) => normalizeRelativePath(file.path) === normalizedSetup)) throw new Error('The selected setup file is not present in the source manifest.');
  const inner = await makeInnerZip(files, onProgress, signal, compressionLevel);
  if (signal?.aborted) throw new DOMException('Canceled', 'AbortError');
  onProgress({ stage: 'encrypting', processed: 0, total: inner.bytes.length, message: 'Encrypting inner package' });
  const encryptionKey = randomBytes(32);
  const macKey = randomBytes(32);
  const iv = randomBytes(16);
  const digest = sha256(inner.bytes);
  const padding = (16 - (inner.bytes.length % 16)) || 16;
  const padded = concat(inner.bytes, new Uint8Array(padding).fill(padding));
  const ciphertext = cbc(encryptionKey, iv, { disablePadding: true }).encrypt(padded);
  const mac = hmac(sha256, macKey, concat(iv, ciphertext));
  onProgress({ stage: 'encrypting', processed: inner.bytes.length, total: inner.bytes.length, message: 'Encryption complete' });
  const encryptedEntry = concat(mac, iv, ciphertext);
  onProgress({ stage: 'assembling', processed: 0, total: encryptedEntry.length, message: 'Writing Intune container' });
  const setupName = setupFile.split('/').pop() || setupFile;
  const detection = makeDetectionXml(setupName, setupFile, inner.bytes.length, encryptionKey, macKey, iv, mac, digest, msiInfo, useMicrosoftCompatibleMetadata);
  const outer = zipSync({
    'IntuneWinPackage/Contents/IntunePackage.intunewin': [encryptedEntry, { level: 0, mtime: ZIP_EPOCH }],
    'IntuneWinPackage/Metadata/Detection.xml': [detection, { level: 0, mtime: ZIP_EPOCH }],
  }, { level: 0, mtime: ZIP_EPOCH });
  onProgress({ stage: 'verifying', processed: 0, total: outer.length, message: 'Verifying package structure' });
  const verified = verifyPackageEnvelope(outer, inner.bytes.length, digest);
  if (!verified.ok) throw new Error(verified.error);
  onProgress({ stage: 'complete', processed: outer.length, total: outer.length, message: 'Package verified' });
  return { bytes: outer, byteLength: outer.length, fileName: outputName, inputBytes: inner.inputBytes, fileCount: files.length, durationMs: performance.now() - started, digest: base64(sha256(outer)), warnings: [] };
}

export function verifyPackage(packageBytes: Uint8Array, setupFile: string): { ok: true } | { ok: false; error: string } {
  try {
    const outer = unzipSync(packageBytes);
    const encrypted = outer['IntuneWinPackage/Contents/IntunePackage.intunewin'];
    const xml = outer['IntuneWinPackage/Metadata/Detection.xml'];
    if (!encrypted || !xml) return { ok: false, error: 'The outer ZIP is missing IntunePackage.intunewin or Detection.xml.' };
    const text = decoder.decode(xml);
    const read = (tag: string) => text.match(new RegExp(`<${tag}>([^<]*)</${tag}>`))?.[1];
    const key = read('EncryptionKey'); const macKey = read('MacKey'); const ivText = read('InitializationVector'); const expectedMac = read('Mac'); const expectedDigest = read('FileDigest'); const declaredSize = Number(read('UnencryptedContentSize'));
    if (!key || !macKey || !ivText || !expectedMac || !expectedDigest || !Number.isSafeInteger(declaredSize)) return { ok: false, error: 'Detection.xml is missing required encryption metadata.' };
    const decode64 = (value: string) => Uint8Array.from(atob(value), (char) => char.charCodeAt(0));
    const actualMac = encrypted.slice(0, 32); const iv = encrypted.slice(32, 48);
    if (base64(iv) !== ivText || base64(actualMac) !== expectedMac) return { ok: false, error: 'The encrypted package header does not match Detection.xml.' };
    const computedMac = hmac(sha256, decode64(macKey), encrypted.slice(32));
    if (base64(computedMac) !== expectedMac) return { ok: false, error: 'The encrypted package MAC is invalid.' };
    const plain = cbc(decode64(key), iv, { disablePadding: true }).decrypt(encrypted.slice(48));
    const padding = plain.at(-1) ?? 0;
    if (!padding || padding > 16 || plain.slice(-padding).some((byte) => byte !== padding)) return { ok: false, error: 'The encrypted package has invalid padding.' };
    const inner = plain.slice(0, -padding);
    if (inner.length !== declaredSize || base64(sha256(inner)) !== expectedDigest) return { ok: false, error: 'The plaintext digest or size is invalid.' };
    const contents = unzipSync(inner);
    if (!contents[setupFile]) return { ok: false, error: 'The selected setup file is not present in the inner package.' };
    return { ok: true };
  } catch (error) { return { ok: false, error: error instanceof Error ? error.message : 'Package verification failed.' }; }
}

/**
 * Fast post-build verification. The inner ZIP was just produced locally and
 * its plaintext digest is already known, so decrypting and inflating it again
 * only duplicates the most expensive work. This verifies the outer container,
 * declared size/digest, encryption header, and HMAC; full verification remains
 * available through verifyPackage for independent qualification.
 */
export function verifyPackageEnvelope(packageBytes: Uint8Array, expectedInnerSize: number, expectedDigest: Uint8Array): { ok: true } | { ok: false; error: string } {
  try {
    const outer = unzipSync(packageBytes);
    const encrypted = outer['IntuneWinPackage/Contents/IntunePackage.intunewin'];
    const xml = outer['IntuneWinPackage/Metadata/Detection.xml'];
    if (!encrypted || !xml) return { ok: false, error: 'The outer ZIP is missing IntunePackage.intunewin or Detection.xml.' };
    const text = decoder.decode(xml);
    const read = (tag: string) => text.match(new RegExp(`<${tag}>([^<]*)</${tag}>`))?.[1];
    const keyText = read('EncryptionKey'); const macKeyText = read('MacKey'); const ivText = read('InitializationVector'); const expectedMac = read('Mac'); const declaredDigest = read('FileDigest'); const declaredSize = Number(read('UnencryptedContentSize'));
    if (!keyText || !macKeyText || !ivText || !expectedMac || !declaredDigest || !Number.isSafeInteger(declaredSize)) return { ok: false, error: 'Detection.xml is missing required encryption metadata.' };
    if (declaredSize !== expectedInnerSize || declaredDigest !== base64(expectedDigest)) return { ok: false, error: 'Detection.xml does not match the generated inner package.' };
    if (encrypted.length < 48) return { ok: false, error: 'The encrypted package header is truncated.' };
    const decode64 = (value: string) => Uint8Array.from(atob(value), (char) => char.charCodeAt(0));
    const actualMac = encrypted.slice(0, 32); const iv = encrypted.slice(32, 48);
    if (base64(iv) !== ivText || base64(actualMac) !== expectedMac) return { ok: false, error: 'The encrypted package header does not match Detection.xml.' };
    const computedMac = hmac(sha256, decode64(macKeyText), encrypted.slice(32));
    if (base64(computedMac) !== expectedMac) return { ok: false, error: 'The encrypted package MAC is invalid.' };
    // Keep the key decoded/validated without performing an unnecessary AES
    // pass; the full verifier covers decrypt/padding when independently run.
    if (decode64(keyText).length !== 32 || decode64(ivText).length !== 16) return { ok: false, error: 'Detection.xml contains invalid encryption key material.' };
    return { ok: true };
  } catch (error) { return { ok: false, error: error instanceof Error ? error.message : 'Package envelope verification failed.' }; }
}

export async function savePackage(result: PackageResult, destination?: FileSystemFileHandle): Promise<'picker' | 'download'> {
  const { bytes, fileName, opfsPath } = result;
  let write: ((chunk: Uint8Array) => Promise<void>) | null = null;
  let close: (() => Promise<void>) | null = null;
  let abort: (() => Promise<void>) | null = null;
  if (destination) {
    const writable = await destination.createWritable();
    write = async (chunk) => writable.write(new Uint8Array(chunk)); close = () => writable.close(); abort = () => writable.abort();
  } else if (!bytes) {
    throw new Error('This browser cannot save the OPFS-backed package. Use a current desktop Edge or Chrome.');
  } else {
    const blob = new Blob([new Uint8Array(bytes)], { type: 'application/octet-stream' }); const url = URL.createObjectURL(blob); const anchor = document.createElement('a'); anchor.href = url; anchor.download = fileName; anchor.click(); setTimeout(() => URL.revokeObjectURL(url), 1000); return 'download';
  }
  try {
    if (opfsPath) {
      const root = await navigator.storage.getDirectory();
      const temp = await root.getFileHandle(opfsPath);
      const file = await temp.getFile();
      const reader = file.stream().getReader();
      try { while (true) { const next = await reader.read(); if (next.done) break; await write!(new Uint8Array(next.value)); } } finally { reader.releaseLock(); }
      await root.removeEntry(opfsPath);
    } else if (bytes) { await write!(new Uint8Array(bytes)); }
    await close!();
  } catch (error) {
    await abort!();
    if (opfsPath) {
      try { const root = await navigator.storage.getDirectory(); await root.removeEntry(opfsPath); } catch { /* Preserve the original error if cleanup is unavailable. */ }
    }
    throw error;
  }
  return 'picker';
}

export function packageImplementationNote(): string { return 'Prototype uses per-file reads. Production large-file support should replace this core with the planned OPFS/WASM streaming pipeline.'; }
